'use client';

import { CatRadioField } from '@blocks-react/components/RadioField';
import { useRadioController } from '../../../../hooks/form-controllers';
import { useFormContext } from 'react-hook-form';
import { useExpander } from '../../../../hooks/useExpander';
import { Conditional } from '../../../common';
import styles from './style.module.scss';
import { CatTextLink } from '@blocks-react/components/TextLink';
import { CatIconPhone } from '@blocks-react/components/Icons/Phone';
import { AssociatedDealerSearchResultExpander } from '../associated-dealer-search-result-expander';
import { CustomerStore } from '../../types';
import { useEffect } from 'react';
import cx from 'classnames';

interface Props {
  name: string;
  store: CustomerStore;
  isFirstValue: boolean;
  isLastValue: boolean;
  disabled: boolean;
}

export const AssociatedDealerSearchResult = ({
  name,
  store,
  isFirstValue,
  isLastValue,
  disabled
}: Props) => {
  const { isExpanded, toggle } = useExpander();
  const handleExpander = () => toggle(!isExpanded);
  const { setValue, resetField, control } = useFormContext();
  const { getRadioField } = useRadioController({ control, name });

  const { line1, line2, line3 } = store.address;

  /**
   * The consumer of the component will provide that isFirstValue flag,
   * we will use that flag to set the provided store prop as the field value
   * and to expand the accordion
   */
  useEffect(() => {
    if (isFirstValue) {
      setValue(name, store.customerNumber);
      handleExpander();
    }

    return () => {
      resetField(name);
    };
  }, [
    isFirstValue,
    name,
    resetField,
    setValue,
    store.customerNumber
  ]);

  return (
    <div
      className={styles[`search-result-container${isLastValue ? '-last' : ''}`]}
    >
      <div className={styles['search-result-content']}>
        <div className={styles['search-result-header']}>
          <CatRadioField
            {...getRadioField(name, store.customerNumber)}
            disabled={disabled}
          >
            <p
              className={cx(
                styles['search-result-radio-field-text'],
                `cat-u-theme-typography-body-sm mb-0`
              )}
            >
              {store.customerNumber}
            </p>
          </CatRadioField>
          <AssociatedDealerSearchResultExpander
            handleExpander={handleExpander}
            isExpanded={isExpanded}
            disabled={disabled}
          />
        </div>
        <div className="my-0">
          <p className="cat-u-theme-typography-label-lg mt-1 mb-0">
            {store.name}
          </p>
          <p className="cat-u-theme-typography-body my-0">
            {store.city} {store.zipcode}
          </p>
        </div>
        <Conditional test={isExpanded}>
          {(!!line1 || !!line2 || !!line3) && (
            <div className="cat-u-theme-typography-body my-0">
              {line1 && <p className="mb-0">{line1}</p>}
              {line2 && <p className="mb-0">{line2}</p>}
              {line3 && <p>{line3}</p>}
            </div>
          )}
          <div className={styles['search-result-expander-content']}>
            <CatTextLink
              href={`tel:${store.phone}`}
              className={`d-flex align-items-center data-cs-mask`}
            >
              <div className="d-flex align-items-center me-2">
                <CatIconPhone />
              </div>
              <span className="cat-u-theme-typography-label data-cs-mask">
                {store.phone}
              </span>
            </CatTextLink>
          </div>
        </Conditional>
      </div>
    </div>
  );
};
